import React, { createContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Task, POI, TargetStatus, TargetType, UndoSnapshot, TaskTemplate, PasteModalState, Target, SimpleTargetValue, FieldResourceTargetValue, FieldResourceEntry, PersonTargetValue } from '../components/Tasks/types';
import { TASK_TYPE_TO_GROUP, GROUP_KEY_TO_TASK_TYPE } from '../components/Tasks/constants';
import { Endpoint, getUserId } from '../api/base';
import { useToast } from './ToastContext';
import { Subject, SubjectType } from '../types/CaseTypes';
import { createNewSubject, createBusinessSubject, relatedSubjectIdFor } from '../components/shared/helpers/newCaseInitValues';
import { FieldResource } from '../types/FieldResourceType';

const uid = () => Math.random().toString(36).substring(2, 11);

// Returns a display string for a target — used in key update notes.
// FIELD_RESOURCE targets store their name in label; all others use value.text.
function getTargetText(target: Target): string {
  if (target.type === 'FIELD_RESOURCE') return target.label;
  if (target.type === 'PERSON') return (target.value as PersonTargetValue).name ?? (target.value as SimpleTargetValue).text ?? target.label ?? '';
  return (target.value as SimpleTargetValue).text ?? '';
}

function normalizeTargetValue(raw: unknown): Target {
  const t = raw as Target;
  if (t.type === 'FIELD_RESOURCE') {
    const val = t.value as Partial<FieldResourceTargetValue>;
    if (!val?.data || typeof val.data !== 'object') {
      return { ...t, value: { data: {} } as FieldResourceTargetValue };
    }
  }
  return t;
}

export interface TasksTabContextValue {
  pois: POI[];
  activePoiId: string | null;
  tasks: Task[];
  selectedTaskId: string | null;
  pasteModal: PasteModalState;
  newTaskSheetOpen: boolean;
  undoSnapshot: UndoSnapshot | null;
  isLoading: boolean;
  fieldResources: FieldResource[];
  fieldResourcesLoading: boolean;
  userMap: Record<string, string>;

  // Derived
  filteredTasks: Task[];
  selectedTask: Task | null;

  setActivePoiId: (id: string | null) => void;
  setSelectedTaskId: (id: string | null) => void;
  openPasteModal: (taskId: string) => void;
  closePasteModal: () => void;
  openNewTaskSheet: () => void;
  closeNewTaskSheet: () => void;

  updateTargetStatus: (taskId: string, targetId: string, status: TargetStatus) => void;
  updateTargetType: (taskId: string, targetId: string, type: TargetType) => void;
  updateTargetNotes: (taskId: string, targetId: string, notes: string) => void;
  updateTargetText: (taskId: string, targetId: string, text: string) => void;
  updateFieldResourceData: (taskId: string, targetId: string, resourceId: string, entry: FieldResourceEntry) => void;
  undo: () => void;
  markAsWinning: (taskId: string, targetId: string) => void;
  pasteTargets: (taskId: string, type: TargetType, rawText: string) => void;
  assignFieldResource: (taskId: string, resourceId: string, resourceName: string) => void;
  createTask: (template: TaskTemplate, poiId: string, customTitle?: string) => void;
  addNote: (taskId: string, note: string) => void;
  createPoi: (firstName: string, lastName: string, subjectType?: SubjectType) => void;
}

export const TasksTabContext = createContext<TasksTabContextValue | null>(null);

function hydrateGroup(task: Task): Task {
  if (task.group_key && task.group_label) return task;
  const taskType = task.task_type ?? GROUP_KEY_TO_TASK_TYPE[task.group_key] ?? 'CUSTOM';
  const group = TASK_TYPE_TO_GROUP[taskType] ?? TASK_TYPE_TO_GROUP['CUSTOM'];
  return { ...task, group_key: group.group_key, group_label: group.group_label };
}

function poisFromTasks(tasks: Task[]): POI[] {
  const seen = new Set<string>();
  const out: POI[] = [];
  for (const t of tasks) {
    if (!seen.has(t.poi.id)) {
      seen.add(t.poi.id);
      out.push(t.poi);
    }
  }
  return out;
}

interface TasksTabProviderProps {
  caseId: string;
  clientId: string;
  subjects?: Subject[];
  onCreateSubject: (subject: Subject) => Promise<void>;
  children: React.ReactNode;
}

export const TasksTabProvider: React.FC<TasksTabProviderProps> = ({ caseId, clientId, subjects = [], onCreateSubject, children }) => {
  const { showToast } = useToast();

  const [tasks, setTasks] = useState<Task[]>([]);
  const tasksRef = useRef<Task[]>([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldResources, setFieldResources] = useState<FieldResource[]>([]);
  const [fieldResourcesLoading, setFieldResourcesLoading] = useState(false);
  const [userMap, setUserMap] = useState<Record<string, string>>({});

  const [activePoiId, setActivePoiId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [undoSnapshot, setUndoSnapshot] = useState<UndoSnapshot | null>(null);
  const [newTaskSheetOpen, setNewTaskSheetOpen] = useState(false);
  const [pasteModal, setPasteModal] = useState<PasteModalState>({ open: false, taskId: '' });

  // ── Derived ────────────────────────────────────────────────────────────────
  const subjectPois = useMemo<POI[]>(() =>
    subjects.map((s) => ({
      id:         s.subject_id,
      name:       s.subject_type === 'business' ? s.business_name : [s.first_name, s.last_name].filter(Boolean).join(' '),
      is_primary: s.is_primary,
    })),
  [subjects]);

  const pois = useMemo<POI[]>(() => {
    const seenIds = new Set(subjectPois.map((p) => p.id));
    const orphanedPois = poisFromTasks(tasks).filter((p) => !seenIds.has(p.id));
    return [...subjectPois, ...orphanedPois];
  }, [subjectPois, tasks]);

  const filteredTasks = useMemo(
    () => activePoiId === null ? tasks : tasks.filter((t) => t.poi.id === activePoiId),
    [tasks, activePoiId]
  );
  const selectedTask = useMemo(
    () => tasks.find((t) => t.task_id === selectedTaskId) ?? null,
    [tasks, selectedTaskId]
  );

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        type RawTask = Omit<Task, 'targets' | 'keyUpdates' | 'milestones'> &
          Partial<Pick<Task, 'targets' | 'keyUpdates'>>;
        const tasksRes = await Endpoint.getTasksForCase(caseId);
        const rawTasks: RawTask[] = tasksRes.data.tasks ?? [];

        const enriched = rawTasks.map((task) =>
          hydrateGroup({
            ...task,
            targets:    (task.targets ?? []).map(normalizeTargetValue),
            keyUpdates: task.keyUpdates ?? [],
            milestones: [],
          } as Task)
        );

        if (!cancelled) {
          setTasks(enriched);
        }
      } catch (err) {
        console.error('Failed to load tasks', err);
        if (!cancelled) showToast('Failed to load tasks', 'error');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field resources (fetched once on mount) ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setFieldResourcesLoading(true);
    Endpoint.getFieldResources()
      .then((res: { data: FieldResource[] }) => {
        if (!cancelled) {
          const all = (res.data as any) ?? [];
          setFieldResources(Array.isArray(all) ? all : []);
        }
      })
      .catch((err: unknown) => { if (!cancelled) console.error('Failed to load field resources', err); })
      .finally(() => { if (!cancelled) setFieldResourcesLoading(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── User directory (id → display name) ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    Endpoint.getUsers()
      .then((res: { data: { Users?: Array<{ Username: string; Attributes: Array<{ Name: string; Value: string }> }> } }) => {
        if (cancelled) return;
        const users = res.data.Users;
        if (!Array.isArray(users) || users.length === 0) {
          console.warn('[TasksTab] getUsers returned no users:', res.data);
          return;
        }
        const map: Record<string, string> = {};
        for (const user of users) {
          // Map Cognito sub UUID → display name (matches created_by_user_id from getUserId())
          const sub = user.Attributes?.find((a) => a.Name === 'sub')?.Value;
          if (sub) map[sub] = user.Username;
          // Also map Attributes[0].Value in case sub isn't the first attribute
          const attr0 = user.Attributes?.[0]?.Value;
          if (attr0 && attr0 !== sub) map[attr0] = user.Username;
          // Also map Username itself — covers cases where created_by_user_id stores the username
          map[user.Username] = user.Username;
        }
        setUserMap(map);
      })
      .catch((err: unknown) => { console.error('[TasksTab] Failed to load user directory:', err); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateTargetStatus = useCallback((taskId: string, targetId: string, status: TargetStatus) => {
    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const target = task?.targets.find((t) => t.target_id === targetId);
    if (!task || !target) return;

    const kuId = uid();
    const now = new Date().toISOString();
    const displayText = getTargetText(target);

    setUndoSnapshot({
      taskId,
      targetId,
      prevStatus: target.status,
      prevUpdatedAt: target.updated_at,
      prevPrimaryTargetIds: task.primary_target_ids,
      removedKeyUpdateId: kuId,
    });

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        const newKeyUpdate = {
          update_id:           kuId,
          task_id:             taskId,
          created_at:          now,
          type:                'target_status_changed' as const,
          target_id:           targetId,
          new_status:          status,
          created_by_user_id:  getUserId(),
          note: `'${displayText}' status → ${status}${status === 'WORKED' ? ' — set as winning lead' : ''}`,
        };
        const ids = t.primary_target_ids;
        const newIds = status === 'WORKED'
          ? ids.includes(targetId) ? ids : [...ids, targetId]
          : ids.filter((id) => id !== targetId);
        return {
          ...t,
          primary_target_ids: newIds,
          targets: t.targets.map((tgt) =>
            tgt.target_id === targetId ? { ...tgt, status, updated_at: now } : tgt
          ),
          keyUpdates: [newKeyUpdate, ...t.keyUpdates].slice(0, 25),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.patchTarget(taskId, targetId, { status, user_id: getUserId() });
        if (status === 'WORKED') {
          await Endpoint.makeTargetPrimary(taskId, targetId);
        }
        const res = await Endpoint.getKeyUpdates(taskId);
        setTasks((prev) =>
          prev.map((task) =>
            task.task_id === taskId
              ? { ...task, keyUpdates: res.data.updates ?? [] }
              : task
          )
        );
      } catch (err) {
        console.error('updateTargetStatus failed', err);
        showToast('Failed to update objective status', 'error');
      }
    })();
  }, [showToast]);

  const updateTargetType = useCallback((taskId: string, targetId: string, type: TargetType) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        return {
          ...t,
          targets: t.targets.map((tgt) =>
            tgt.target_id === targetId ? { ...tgt, type, updated_at: new Date().toISOString() } : tgt
          ),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.patchTarget(taskId, targetId, { type });
      } catch (err) {
        console.error('updateTargetType failed', err);
        showToast('Failed to update objective type', 'error');
      }
    })();
  }, [showToast]);

  const updateFieldResourceData = useCallback((
    taskId: string,
    targetId: string,
    resourceId: string,
    entry: FieldResourceEntry,
  ) => {
    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const target = task?.targets.find((t) => t.target_id === targetId);
    if (!task || !target) return;

    const existing = target.value as FieldResourceTargetValue;
    const newValue: FieldResourceTargetValue = {
      data: { ...existing.data, [resourceId]: entry },
    };

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        return {
          ...t,
          targets: t.targets.map((tgt) =>
            tgt.target_id === targetId ? { ...tgt, value: newValue, updated_at: new Date().toISOString() } : tgt
          ),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.patchTarget(taskId, targetId, { value: newValue });
      } catch (err) {
        console.error('updateFieldResourceData failed', err);
        showToast('Failed to update field resource data', 'error');
      }
    })();
  }, [showToast]);

  const updateTargetNotes = useCallback((taskId: string, targetId: string, notes: string) => {
    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const target = task?.targets.find((t) => t.target_id === targetId);
    if (!task || !target) return;
    if (target.type === 'FIELD_RESOURCE') return;

    const newValue = { ...target.value, notes };

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        return {
          ...t,
          targets: t.targets.map((tgt) =>
            tgt.target_id === targetId ? { ...tgt, value: newValue, updated_at: new Date().toISOString() } : tgt
          ),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.patchTarget(taskId, targetId, { value: newValue });
      } catch (err) {
        console.error('updateTargetNotes failed', err);
        showToast('Failed to save notes', 'error');
      }
    })();
  }, [showToast]);

  const updateTargetText = useCallback((taskId: string, targetId: string, text: string) => {
    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const target = task?.targets.find((t) => t.target_id === targetId);
    if (!task || !target) return;
    if (target.type === 'FIELD_RESOURCE') return;

    const newValue = { ...target.value as SimpleTargetValue, text };

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        return {
          ...t,
          targets: t.targets.map((tgt) =>
            tgt.target_id === targetId
              ? { ...tgt, label: text, value: newValue, updated_at: new Date().toISOString() }
              : tgt
          ),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.patchTarget(taskId, targetId, { value: newValue, label: text });
      } catch (err) {
        console.error('updateTargetText failed', err);
        showToast('Failed to save objective', 'error');
      }
    })();
  }, [showToast]);

  const undo = useCallback(() => {
    if (!undoSnapshot) return;
    const { taskId, targetId, prevStatus, prevUpdatedAt, prevPrimaryTargetIds, removedKeyUpdateId } = undoSnapshot;

    setTasks((prev) =>
      prev.map((task) => {
        if (task.task_id !== taskId) return task;
        return {
          ...task,
          primary_target_ids: prevPrimaryTargetIds,
          targets: task.targets.map((t) =>
            t.target_id === targetId
              ? { ...t, status: prevStatus, updated_at: prevUpdatedAt ?? t.updated_at }
              : t
          ),
          keyUpdates: task.keyUpdates.filter((ku) => ku.update_id !== removedKeyUpdateId),
        };
      })
    );
    setUndoSnapshot(null);
  }, [undoSnapshot]);

  const markAsWinning = useCallback((taskId: string, targetId: string) => {
    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const target = task?.targets.find((t) => t.target_id === targetId);

    const kuId = uid();
    const now = new Date().toISOString();
    const displayText = target ? getTargetText(target) : targetId;

    const isAlreadyWinning = task?.primary_target_ids.includes(targetId) ?? false;

    setUndoSnapshot({
      taskId,
      targetId,
      prevStatus: target?.status ?? 'NEW',
      prevUpdatedAt: target?.updated_at,
      prevPrimaryTargetIds: task?.primary_target_ids ?? [],
      removedKeyUpdateId: kuId,
    });

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        const alreadyInState = t.primary_target_ids.includes(targetId);
        const newIds = alreadyInState
          ? t.primary_target_ids.filter((id) => id !== targetId)
          : [...t.primary_target_ids, targetId];
        const newKeyUpdate = {
          update_id:           kuId,
          task_id:             taskId,
          created_at:          now,
          type:                'target_made_primary' as const,
          target_id:           targetId,
          created_by_user_id:  getUserId(),
          note:                isAlreadyWinning
            ? `'${displayText}' removed from winning leads`
            : `'${displayText}' set as winning lead`,
        };
        return {
          ...t,
          primary_target_ids: newIds,
          keyUpdates: [newKeyUpdate, ...t.keyUpdates].slice(0, 25),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.makeTargetPrimary(taskId, targetId, { user_id: getUserId() });
        const res = await Endpoint.getKeyUpdates(taskId);
        setTasks((prev) =>
          prev.map((task) =>
            task.task_id === taskId
              ? { ...task, keyUpdates: res.data.updates ?? [] }
              : task
          )
        );
      } catch (err) {
        console.error('markAsWinning failed', err);
        showToast('Failed to update winning lead', 'error');
      }
    })();
  }, [showToast]);

  const pasteTargets = useCallback((taskId: string, type: TargetType, rawText: string) => {
    const lines = rawText.split('\n').map((l) => l.trim()).filter(Boolean);
    const now = new Date().toISOString();

    const task = tasksRef.current.find((t) => t.task_id === taskId);
    const existingValues = new Set(
      (task?.targets ?? [])
        .filter((t) => t.type !== 'FIELD_RESOURCE')
        .map((t) => ((t.value as SimpleTargetValue).text ?? '').toLowerCase())
    );
    const seenInPaste = new Set<string>();
    const newLines = lines.filter((line) => {
      const key = line.toLowerCase();
      if (existingValues.has(key) || seenInPaste.has(key)) return false;
      seenInPaste.add(key);
      return true;
    });

    setTasks((prev) =>
      prev.map((t) => {
        if (t.task_id !== taskId) return t;
        const newTargets = newLines.map((line) => ({
          target_id:  uid(),
          task_id:    taskId,
          label:      line,
          value:      ({ text: line } as SimpleTargetValue),
          type,
          status:     'NEW' as const,
          created_at: now,
          updated_at: now,
        }));
        return { ...t, targets: [...t.targets, ...newTargets] };
      })
    );
    setPasteModal({ open: false, taskId: '' });

    (async () => {
      try {
        await Endpoint.batchAddTargets(taskId, { raw: newLines.join('\n'), type, user_id: getUserId(), case_id: caseId });
        const [targetsRes, kuRes] = await Promise.all([
          Endpoint.getTargets(taskId),
          Endpoint.getKeyUpdates(taskId),
        ]);
        setTasks((prev) =>
          prev.map((task) =>
            task.task_id === taskId
              ? {
                  ...task,
                  targets:    (targetsRes.data.targets ?? []).map(normalizeTargetValue),
                  keyUpdates: kuRes.data.updates ?? [],
                }
              : task
          )
        );
      } catch (err) {
        console.error('pasteTargets failed', err);
        showToast('Failed to add objectives', 'error');
      }
    })();
  }, [showToast]);

  const assignFieldResource = useCallback((taskId: string, resourceId: string, resourceName: string) => {
    const now = new Date().toISOString();
    const kuId = uid();

    setTasks((prev) =>
      prev.map((task) => {
        if (task.task_id !== taskId) return task;
        const alreadyAssigned = task.targets
          .filter((t) => t.type === 'FIELD_RESOURCE')
          .some((t) => resourceId in ((t.value as FieldResourceTargetValue).data ?? {}));
        if (alreadyAssigned) return task;
        const optimisticKu = {
          update_id:           kuId,
          task_id:             taskId,
          created_at:          now,
          type:                'targets_added' as const,
          note:                `Added field resource: ${resourceName}`,
          created_by_user_id:  getUserId(),
        };
        return {
          ...task,
          targets: [
            ...task.targets,
            {
              target_id:  uid(),
              task_id:    taskId,
              label:      resourceName,
              value:      { data: { [resourceId]: {} } } as FieldResourceTargetValue,
              type:       'FIELD_RESOURCE' as const,
              status:     'NEW' as const,
              created_at: now,
              updated_at: now,
            },
          ],
          keyUpdates: [optimisticKu, ...task.keyUpdates].slice(0, 25),
        };
      })
    );

    (async () => {
      try {
        await Endpoint.batchAddTargets(taskId, {
          raw:     resourceId,
          type:    'FIELD_RESOURCE',
          label:   resourceName,
          user_id: getUserId(),
          case_id: caseId,
        });
        const [targetsRes, kuRes] = await Promise.all([
          Endpoint.getTargets(taskId),
          Endpoint.getKeyUpdates(taskId),
        ]);
        setTasks((prev) =>
          prev.map((task) =>
            task.task_id === taskId
              ? {
                  ...task,
                  targets:    (targetsRes.data.targets ?? []).map(normalizeTargetValue),
                  keyUpdates: kuRes.data.updates ?? [],
                }
              : task
          )
        );
      } catch (err) {
        console.error('assignFieldResource failed', err);
        showToast('Failed to assign field resource', 'error');
      }
    })();
  }, [showToast]);

  const openNewTaskSheet = useCallback(() => setNewTaskSheetOpen(true), []);
  const closeNewTaskSheet = useCallback(() => setNewTaskSheetOpen(false), []);

  const createTask = useCallback((template: TaskTemplate, poiId: string, customTitle?: string) => {
    const poi = pois.find((p) => p.id === poiId);
    if (!poi) {
      showToast('Could not find the selected subject. Please try again.', 'error');
      return;
    }
    const now = new Date().toISOString();
    const tempId = `temp_${uid()}`;
    const group = TASK_TYPE_TO_GROUP[template.task_type] ?? TASK_TYPE_TO_GROUP['CUSTOM'];

    const optimisticTask: Task = {
      task_id:             tempId,
      task_type:           template.task_type,
      title:               customTitle || template.label,
      description:         '',
      notes:               '',
      case_id:             caseId,
      client_id:           clientId,
      status:              'TODO',
      is_starred:          false,
      group_key:           group.group_key,
      group_label:         group.group_label,
      poi,
      assigned_to_user_id: '',
      created_by_user_id:  getUserId(),
      created_at:          now,
      updated_at:          now,
      progress_done:       '0',
      progress_total:      '0',
      primary_target_ids:  [],
      version:             '1',
      targets:             [],
      keyUpdates:          [],
      milestones:          [],
    };

    setTasks((prev) => [...prev, optimisticTask]);
    setActivePoiId(poiId);
    setSelectedTaskId(tempId);
    closeNewTaskSheet();

    (async () => {
      try {
        const res = await Endpoint.createTask({
          case_id:   caseId,
          client_id: clientId,
          user_id:   getUserId(),
          title:     customTitle || template.label,
          task_type: template.task_type,
          poi,
          status:    'TODO',
        });
        const created: Task = hydrateGroup({
          ...res.data.task,
          targets:    [],
          keyUpdates: [],
          milestones: [],
        });
        setTasks((prev) =>
          prev.map((t) => (t.task_id === tempId ? created : t))
        );
        setSelectedTaskId(created.task_id);
        const kuRes = await Endpoint.getKeyUpdates(created.task_id);
        setTasks((prev) =>
          prev.map((t) =>
            t.task_id === created.task_id
              ? { ...t, keyUpdates: kuRes.data.updates ?? [] }
              : t
          )
        );
      } catch (err) {
        console.error('createTask failed', err);
        showToast('Failed to create task', 'error');
        setTasks((prev) => prev.filter((t) => t.task_id !== tempId));
        setSelectedTaskId(null);
      }
    })();
  }, [pois, caseId, clientId, closeNewTaskSheet, showToast]);

  const addNote = useCallback((taskId: string, note: string) => {
    if (!note.trim()) return;
    const now = new Date().toISOString();
    const kuId = uid();

    const optimisticKu = {
      update_id:           kuId,
      task_id:             taskId,
      created_at:          now,
      type:                'note' as const,
      note:                note.trim(),
      created_by_user_id:  getUserId(),
    };

    setTasks((prev) =>
      prev.map((task) => {
        if (task.task_id !== taskId) return task;
        return { ...task, keyUpdates: [optimisticKu, ...task.keyUpdates].slice(0, 25) };
      })
    );

    (async () => {
      try {
        await Endpoint.createKeyUpdate(taskId, { type: 'note', note: note.trim(), user_id: getUserId(), case_id: caseId });
        const res = await Endpoint.getKeyUpdates(taskId);
        setTasks((prev) =>
          prev.map((task) =>
            task.task_id === taskId
              ? { ...task, keyUpdates: res.data.updates ?? [] }
              : task
          )
        );
      } catch (err) {
        console.error('addNote failed', err);
        showToast('Failed to add note', 'error');
      }
    })();
  }, [showToast]);

  const createPoi = useCallback((firstName: string, lastName: string, subjectType: SubjectType = 'person') => {
    const newSubject: Subject = subjectType === 'business'
      ? { ...createBusinessSubject(), business_name: firstName.trim() }
      : {
          ...createNewSubject(false, relatedSubjectIdFor(subjects)),
          first_name: firstName.trim(),
          last_name:  lastName.trim(),
        };
    setActivePoiId(newSubject.subject_id);
    onCreateSubject(newSubject);
  }, [subjects, onCreateSubject]);

  const openPasteModal = useCallback((taskId: string) => setPasteModal({ open: true, taskId }), []);
  const closePasteModal = useCallback(() => setPasteModal({ open: false, taskId: '' }), []);

  return (
    <TasksTabContext.Provider
      value={{
        pois, activePoiId, tasks, selectedTaskId,
        pasteModal, newTaskSheetOpen, undoSnapshot, isLoading,
        fieldResources, fieldResourcesLoading, userMap,
        filteredTasks, selectedTask,
        setActivePoiId, setSelectedTaskId,
        openPasteModal, closePasteModal,
        openNewTaskSheet, closeNewTaskSheet,
        updateTargetStatus, updateTargetType, updateTargetNotes, updateTargetText, updateFieldResourceData,
        undo, markAsWinning,
        pasteTargets, assignFieldResource, createTask, addNote, createPoi,
      }}
    >
      {children}
    </TasksTabContext.Provider>
  );
};

